import { generateUUID } from "@/utils/uuid";
import type { IDBPDatabase } from "idb";

import type { ClaimQueueEntry, ClaimQueueEntryStatus } from "./types";

const STORE_NAME = "claim-queue";

type ClaimQueueListener = () => void;

class ClaimQueueManager {
  private db: IDBPDatabase | null = null;
  private listeners: ClaimQueueListener[] = [];

  setDatabase(db: IDBPDatabase): void {
    this.db = db;
    void this.resetStuckSyncing();
  }

  async add(
    entry: Omit<
      ClaimQueueEntry,
      "id" | "createdAt" | "retryCount" | "lastRetryAt" | "status"
    >
  ): Promise<ClaimQueueEntry | null> {
    if (!this.db) {
      return null;
    }

    const existing = await this.findByMeasurementId(entry.measurementId);
    if (existing) {
      return this.update(existing.id, {
        phoneNumber: entry.phoneNumber,
        introText: entry.introText,
        status: "pending",
        retryCount: 0,
        lastRetryAt: null,
      });
    }

    const newEntry: ClaimQueueEntry = {
      id: generateUUID(),
      measurementId: entry.measurementId,
      phoneNumber: entry.phoneNumber,
      introText: entry.introText,
      createdAt: Date.now(),
      retryCount: 0,
      lastRetryAt: null,
      status: "pending",
    };

    try {
      await this.db.put(STORE_NAME, newEntry);
      this.notifyListeners();
      return newEntry;
    } catch (error) {
      console.error("[ClaimQueue] Failed to add entry:", error);
      return null;
    }
  }

  async remove(id: string): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      await this.db.delete(STORE_NAME, id);
      this.notifyListeners();
    } catch (error) {
      console.error("[ClaimQueue] Failed to remove entry:", error);
    }
  }

  async getAll(): Promise<ClaimQueueEntry[]> {
    if (!this.db) {
      return [];
    }

    try {
      return await this.db.getAll(STORE_NAME);
    } catch (error) {
      console.error("[ClaimQueue] Failed to get all entries:", error);
      return [];
    }
  }

  async getPending(): Promise<ClaimQueueEntry[]> {
    if (!this.db) {
      return [];
    }

    try {
      const entries = await this.db.getAllFromIndex(
        STORE_NAME,
        "status",
        "pending"
      );
      return entries.sort((a, b) => a.createdAt - b.createdAt);
    } catch (error) {
      console.error("[ClaimQueue] Failed to get pending entries:", error);
      return [];
    }
  }

  async getSyncable(): Promise<ClaimQueueEntry[]> {
    if (!this.db) {
      return [];
    }

    try {
      const [pending, failed] = await Promise.all([
        this.db.getAllFromIndex(STORE_NAME, "status", "pending"),
        this.db.getAllFromIndex(STORE_NAME, "status", "failed"),
      ]);
      return [...pending, ...failed].sort((a, b) => a.createdAt - b.createdAt);
    } catch (error) {
      console.error("[ClaimQueue] Failed to get syncable entries:", error);
      return [];
    }
  }

  async count(): Promise<number> {
    if (!this.db) {
      return 0;
    }

    try {
      return await this.db.count(STORE_NAME);
    } catch (error) {
      console.error("[ClaimQueue] Failed to count entries:", error);
      return 0;
    }
  }

  async clear(): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      await this.db.clear(STORE_NAME);
      this.notifyListeners();
    } catch (error) {
      console.error("[ClaimQueue] Failed to clear entries:", error);
    }
  }

  async updateStatus(id: string, status: ClaimQueueEntryStatus): Promise<void> {
    await this.update(id, {
      status,
      ...(status === "syncing" ? {} : {}),
      ...(status === "failed" ? { lastRetryAt: Date.now() } : {}),
    });
  }

  async markSyncing(id: string): Promise<void> {
    await this.updateStatus(id, "syncing");
  }

  async markFailed(id: string): Promise<void> {
    const entry = await this.getById(id);
    if (!entry) {
      return;
    }

    await this.update(id, {
      status: "failed",
      retryCount: entry.retryCount + 1,
      lastRetryAt: Date.now(),
    });
  }

  async markPending(id: string): Promise<void> {
    await this.updateStatus(id, "pending");
  }

  addListener(listener: ClaimQueueListener): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  private async getById(id: string): Promise<ClaimQueueEntry | null> {
    if (!this.db) {
      return null;
    }

    try {
      const entry = await this.db.get(STORE_NAME, id);
      return (entry as ClaimQueueEntry) ?? null;
    } catch (error) {
      console.error("[ClaimQueue] Failed to get entry:", error);
      return null;
    }
  }

  private async findByMeasurementId(
    measurementId: string
  ): Promise<ClaimQueueEntry | null> {
    if (!this.db) {
      return null;
    }

    try {
      const entry = await this.db.getFromIndex(
        STORE_NAME,
        "measurementId",
        measurementId
      );
      return (entry as ClaimQueueEntry) ?? null;
    } catch (error) {
      console.error("[ClaimQueue] Failed to find by measurementId:", error);
      return null;
    }
  }

  private async update(
    id: string,
    fields: Partial<ClaimQueueEntry>
  ): Promise<ClaimQueueEntry | null> {
    if (!this.db) {
      return null;
    }

    const existing = await this.getById(id);
    if (!existing) {
      return null;
    }

    const updated = { ...existing, ...fields };

    try {
      await this.db.put(STORE_NAME, updated);
      this.notifyListeners();
      return updated;
    } catch (error) {
      console.error("[ClaimQueue] Failed to update entry:", error);
      return null;
    }
  }

  private notifyListeners(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }

  async resetStuckSyncing(): Promise<void> {
    if (!this.db) {
      return;
    }

    try {
      const syncing = await this.db.getAllFromIndex(
        STORE_NAME,
        "status",
        "syncing"
      );
      await Promise.all(
        syncing.map((entry) => this.update(entry.id, { status: "pending" }))
      );
      if (syncing.length > 0) {
        console.log(
          `[ClaimQueue] Reset ${syncing.length} stuck syncing entry(s) to pending`
        );
      }
    } catch (error) {
      console.error(
        "[ClaimQueue] Failed to reset stuck syncing entries:",
        error
      );
    }
  }
}

export const claimQueueManager = new ClaimQueueManager();
